import config from '../../config/config';
import axios from 'axios';
import { Auth0ContextInterface } from '@auth0/auth0-react';
import {
  LeadAPI,
  LeadBulkCreatePayload,
  LeadCustomFieldAPI,
  LeadCustomFieldPayload,
  LeadDataProps,
  MDLead,
} from '@lemontaps/lib/types/api';
import { getAuth0TokenScopeString } from '@/util';
import { withSilentAccessToken } from '../helper';
import { PERMISSIONS } from '../constants';
import { Filter } from '@lemontaps/lib/util';
import { UpstreamLeadObjectType } from '@lemontaps/lib/types/global';

export const getAccountLeads = async (
  accountId: string,
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  options = {},
) => {
  const token = await getAccessTokenSilently({
    authorizationParams: {
      audience: import.meta.env.REACT_APP_AUTH0_API_AUDIENCE,
      scope: getAuth0TokenScopeString(),
    },
  });
  const { data } = await axios.get(config.API_BASE_URL + `lead-gen/accounts/${accountId}/leads`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: options,
  });

  if (!data.isSuccess) throw new Error('Failed to fetch leads');
  return data.data;
};

export const getLeadsByIds = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  accountId: string,
  leadIds: number[],
) => {
  const token = await getAccessTokenSilently({
    authorizationParams: {
      audience: import.meta.env.REACT_APP_AUTH0_API_AUDIENCE,
      scope: getAuth0TokenScopeString(),
    },
  });

  const { data } = await axios.get(
    config.API_BASE_URL + `lead-gen/accounts/${accountId}/leadsByIds`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        leadIds,
      },
    },
  );

  if (!data.isSuccess) throw new Error('Failed to fetch leads');
  return data.data;
};

export const getSingleLead = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  accountId: string,
  leadId: string,
) => {
  const { data: response } = await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.get(config.API_BASE_URL + `lead-gen/accounts/${accountId}/leads/${leadId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
  );
  if (!response.isSuccess) throw new Error('Failed to fetch lead');
  return response.data;
};

export const getLeadLTAccount = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  username: string,
) => {
  const { data: res } = await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.get(config.API_BASE_URL + `account/${username}?crossDomain=true`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
  );

  if (!res.isSuccess) throw new Error('Failed to fetch lead account');
  return res.data;
};

export const getLeadCustomFields = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  accountId: string,
): Promise<LeadCustomFieldAPI[]> => {
  const { data: response } = await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.get(config.API_BASE_URL + `lead-gen/accounts/${accountId}/lead-custom-fields`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
  );
  if (!response.isSuccess) throw new Error('Failed to fetch lead custom fields');
  return response.data;
};

export const createLeadCustomFields = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  payload: LeadCustomFieldPayload,
): Promise<LeadCustomFieldAPI> => {
  const { data: response } = await withSilentAccessToken(
    getAccessTokenSilently,
    token =>
      axios.post(config.API_BASE_URL + `lead-gen/accounts/lead-custom-fields`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    [PERMISSIONS.WRITE.THEME_GLOBAL],
  );
  if (!response.isSuccess) throw new Error('Failed to create lead custom field');
  return response.data;
};

export const updateLeadCustomFields = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  payload: LeadCustomFieldPayload,
): Promise<LeadCustomFieldAPI> => {
  const { data: response } = await withSilentAccessToken(
    getAccessTokenSilently,
    token =>
      axios.put(
        config.API_BASE_URL + `lead-gen/accounts/lead-custom-fields/` + payload.id,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      ),
    [PERMISSIONS.WRITE.THEME_GLOBAL],
  );
  if (!response.isSuccess) throw new Error('Failed to update lead custom field');
  return response.data;
};

export const deleteLeadCustomFields = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  id: number,
): Promise<void> => {
  await withSilentAccessToken(
    getAccessTokenSilently,
    token =>
      axios.delete(config.API_BASE_URL + 'lead-gen/accounts/lead-custom-fields/' + id, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    [PERMISSIONS.WRITE.THEME_GLOBAL],
  );
};

export const addLead = async (
  leadData: LeadDataProps | Partial<Record<keyof LeadAPI, string>>,
  id: string,
  {
    external = false,
    uid,
  }: {
    external?: boolean;
    uid?: number;
  } = {},
) => {
  const response = await axios.post(
    config.API_BASE_URL + `lead-gen/accounts/${id}/leads`,
    {
      ...leadData,
      external,
    },
    { params: { uid } },
  );
  return response;
};

export const editLead = async (
  leadData: LeadDataProps,
  id: string,
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
) => {
  const token = await getAccessTokenSilently({
    authorizationParams: {
      audience: import.meta.env.REACT_APP_AUTH0_API_AUDIENCE,
      scope: getAuth0TokenScopeString(),
    },
  });

  const response = await axios.patch(
    config.API_BASE_URL + `lead-gen/accounts/${id}/leads/${leadData.id}`,
    {
      ...leadData,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response;
};

export const deleteAccountLead = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  accountId: string,
  leadId: string,
) => {
  const token = await getAccessTokenSilently({
    authorizationParams: {
      audience: import.meta.env.REACT_APP_AUTH0_API_AUDIENCE,
      scope: getAuth0TokenScopeString(),
    },
  });

  const { data: response } = await axios.delete(
    config.API_BASE_URL + `lead-gen/accounts/${accountId}/leads/${leadId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (!response.isSuccess) throw new Error('Failed to fetch lead options types');
};

export const fetchDownloadLeads = async (
  firstName: string,
  fileType: string,
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  options = {},
) => {
  const token = await getAccessTokenSilently({
    authorizationParams: {
      audience: import.meta.env.REACT_APP_AUTH0_API_AUDIENCE,
      scope: getAuth0TokenScopeString(),
    },
  });

  const anchor = document.createElement('a');
  document.body.appendChild(anchor);

  axios
    .get(config.API_BASE_URL + `lead-gen/accounts/leads/${fileType}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: options,
      responseType: 'blob',
    })
    .then(res => {
      const blob = res.data;
      const objectUrl = window.URL.createObjectURL(blob);

      anchor.href = objectUrl;
      anchor.download = `${firstName}-contacts.${fileType}`;
      anchor.click();

      window.URL.revokeObjectURL(objectUrl);
    });
};

export const scanBusinessCard = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  payload: FormData,
  accountId: string,
) =>
  await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.post(config.API_BASE_URL + `lead-gen/accounts/${accountId}/scan`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
  );

export const exportLeadsToCrm = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  payload: {
    leadIds: number[];
    serviceId: string;
    campaignId?: string;
    upstreamLeadObjectType?: UpstreamLeadObjectType | null;
  },
) =>
  await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.post(config.API_BASE_URL + `lead-gen/accounts/leads/export`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
  );

export const exportLeadsToSheet = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  exportFormat: 'csv' | 'xlsx',
  queryParams?: { leadIds: number[] },
) =>
  await withSilentAccessToken(
    getAccessTokenSilently,
    token =>
      axios.get(config.API_BASE_URL + `theme/leads/export/${exportFormat}`, {
        params: queryParams || {},
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      }),
    [PERMISSIONS.READ.THEME_PROFILES],
  );

export const getMdLeads = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  queryParams = {},
): Promise<{
  leads?: Array<MDLead>;
  leadIds: { id: number }[];
  total?: number;
  page?: number;
}> => {
  const { data: result } = await withSilentAccessToken(
    getAccessTokenSilently,
    token =>
      axios.get(config.API_BASE_URL + `theme/contacts`, {
        params: queryParams,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    [PERMISSIONS.READ.THEME_PROFILES],
  );

  if (!result.isSuccess || !result?.data?.leads) {
    throw new Error('Failed to fetch leads');
  }
  return {
    leads: result.data.leads || [],
    leadIds: result.data.leadIds || [],
    total: result.data.total || 0,
    page: result.data.page || 0,
  };
};

export const deleteMdLeads = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  ids: number[],
) => {
  // send delete request
  const { data } = await withSilentAccessToken(
    getAccessTokenSilently,
    token =>
      axios.post(
        config.API_BASE_URL + `business/bulkDeleteContacts/`,
        { ids },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      ),
    [PERMISSIONS.WRITE.THEME_PROFILES],
  );

  if (!data.isSuccess) {
    throw new Error('Failed to delete md lead');
  }

  return data;
};

export const editMdLead = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  payload: Partial<LeadAPI>,
) => {
  const { data } = await withSilentAccessToken(
    getAccessTokenSilently,
    token =>
      axios.patch(config.API_BASE_URL + `theme/contacts/${payload.id}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    [PERMISSIONS.WRITE.THEME_PROFILES],
  );
  if (!data.isSuccess) {
    throw new Error('Failed to edit md lead');
  }
  return data;
};

export const getLeadFilters = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  options: {
    singleUser: boolean;
    language: string;
  },
): Promise<Filter[]> => {
  const { data: result } = await withSilentAccessToken(
    getAccessTokenSilently,
    token =>
      axios.get(config.API_BASE_URL + `theme/contacts/filters`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: options,
      }),
    [PERMISSIONS.READ.THEME_PROFILES],
  );

  return result;
};

export const enrichLeads = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  leadIds: number[],
  accountId: string,
) =>
  await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.post(
      config.API_BASE_URL + `lead-gen/accounts/${accountId}/enrich`,
      { leadIds },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    ),
  );

export const scrapeLeadsLinkedIn = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  leadIds: number[],
  accountId: string,
) =>
  await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.post(
      config.API_BASE_URL + `lead-gen/accounts/${accountId}/scrape`,
      { leadIds },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    ),
  );

export const checkEnrichmentStatus = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  leadIds: number[],
) =>
  await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.get(config.API_BASE_URL + `lead-gen/accounts/check-enrichment-status`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        leadIds,
      },
    }),
  );

export const checkScrapingStatus = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  leadIds: number[],
) =>
  await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.get(config.API_BASE_URL + `lead-gen/accounts/check-scraping-status`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        leadIds,
      },
    }),
  );

export const bulkCreateLeads = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  payload: LeadBulkCreatePayload[],
  isSingleUser?: boolean,
) =>
  await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.post(config.API_BASE_URL + `lead-gen/accounts/leads/bulk`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: { isSingleUser },
    }),
  );

export const sendFollowUpEmails = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  leadIds: number[],
) =>
  await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.post(
      config.API_BASE_URL + `lead-gen/accounts/send-follow-up-emails`,
      { leadIds },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    ),
  );

export const downloadSignature = async (
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  signatureCustomFieldValueId: number,
) =>
  await withSilentAccessToken(getAccessTokenSilently, token =>
    axios.get(
      config.API_BASE_URL +
        `lead-gen/accounts/lead-custom-fields/${signatureCustomFieldValueId}/download-signature`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'arraybuffer',
      },
    ),
  );
